import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import {
  Building2, LayoutDashboard, Home, FileText, MessageSquare,
  Wrench, Calendar, Bot, LogOut, Menu, X, User
} from 'lucide-react';

export default function Layout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navigation = [
    { name: 'Dashboard', path: '/dashboard', icon: <LayoutDashboard className="w-5 h-5" />, roles: ['admin', 'agent', 'landlord', 'tenant'] },
    { name: 'Properties', path: '/properties', icon: <Home className="w-5 h-5" />, roles: ['admin', 'agent', 'landlord', 'tenant'] },
    { name: 'Leases', path: '/leases', icon: <FileText className="w-5 h-5" />, roles: ['admin', 'agent'] },
    { name: 'Tenants', path: '/tenants', icon: <User className="w-5 h-5" />, roles: ['admin', 'agent'] },
    { name: 'Landlords', path: '/landlords', icon: <User className="w-5 h-5" />, roles: ['admin', 'agent'] },
    { name: 'Inbox', path: '/inbox', icon: <MessageSquare className="w-5 h-5" />, roles: ['admin', 'agent', 'landlord', 'tenant'] },
    { name: 'Maintenance', path: '/maintenance', icon: <Wrench className="w-5 h-5" />, roles: ['admin', 'agent', 'landlord', 'tenant'] },
    { name: 'Inspections', path: '/inspections', icon: <Calendar className="w-5 h-5" />, roles: ['admin', 'agent'] },
    { name: 'AI Activity', path: '/ai-activity', icon: <Bot className="w-5 h-5" />, roles: ['admin', 'agent'] },
  ];

  const filteredNav = navigation.filter(item => item.roles.includes(user?.role));

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 h-full w-64 bg-white shadow-xl z-50 flex flex-col
          transform transition-transform duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0
        `}
      >
        <div className="p-6 border-b">
          <div className="flex items-center gap-2">
            <Building2 className="w-8 h-8 text-blue-600" />
            <div>
              <h1 className="text-xl font-bold text-gray-900">PropFlow AI</h1>
              <p className="text-xs text-gray-500">{user?.role}</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto p-4 space-y-2" style={{ maxHeight: 'calc(100vh - 280px)' }}>
          {filteredNav.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <button
                key={item.path}
                data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                onClick={() => {
                  navigate(item.path);
                  setSidebarOpen(false);
                }}
                className={`
                  w-full flex items-center gap-3 px-4 py-3 rounded-lg
                  font-medium transition-colors
                  ${isActive
                    ? 'bg-blue-50 text-blue-600'
                    : 'text-gray-700 hover:bg-gray-50'
                  }
                `}
              >
                {item.icon}
                {item.name}
              </button>
            );
          })}
        </nav>

        <div className="mt-auto p-4 border-t bg-white">
          <div className="flex items-center gap-3 px-4 py-3 mb-2">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-full flex items-center justify-center text-white font-semibold">
              {user?.name?.charAt(0) || <User className="w-5 h-5" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{user?.name}</p>
              <p className="text-xs text-gray-500 truncate">{user?.email}</p>
              <p className="text-xs text-blue-600 font-medium capitalize mt-1">{user?.role}</p>
            </div>
          </div>
          <Button
            data-testid="logout-btn"
            variant="outline"
            className="w-full"
            onClick={handleLogout}
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:pl-64">
        {/* Mobile Header */}
        <header className="lg:hidden bg-white shadow-sm px-4 py-3 flex items-center justify-between">
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-lg hover:bg-gray-100"
          >
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
          <div className="flex items-center gap-2">
            <Building2 className="w-6 h-6 text-blue-600" />
            <span className="font-bold text-gray-900">PropFlow AI</span>
          </div>
          <div className="w-10" />
        </header>

        {/* Page Content */}
        <main className="p-6 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
}